<script setup lang="ts">
import { isJust, just, nothing, type Maybe } from "../../lib/maybe";
/**
 * Thesaurus dialog search and results.
 */
import { onUnmounted, ref, useTemplateRef } from "vue";
import { createTimeoutHandle } from "../../lib/timeoutHandle";
import { posLabel } from "../../lib/wordLookup";
import { useWordLookupStore } from "../../stores/useWordLookupStore";
import ZwInput from "../ui/ZwInput.vue";

const props = withDefaults(
  defineProps<{
    /** Compact single-row search suitable for the dialog. */
    compactSearch?: boolean;
  }>(),
  { compactSearch: false },
);

const emit = defineEmits<{
  applied: [];
}>();

const lookup = useWordLookupStore();
const searchInput = useTemplateRef<InstanceType<typeof ZwInput>>("searchInput");

/** Chip key currently showing the brief "Copied!" bubble. */
const copiedKey = ref<Maybe<string>>(nothing);
const copiedTimer = createTimeoutHandle();

function onSubmit(): void {
  void lookup.submitSearch();
}

function chipKey(
  kind: "syn" | "ant",
  senseIndex: number,
  word: string,
): string {
  return `${kind}:${senseIndex}:${word}`;
}

function chipTitle(word: string): string {
  return lookup.chipsReplaceEditor ? `Use “${word}”` : `Copy “${word}”`;
}

function isCopied(key: string): boolean {
  return isJust(copiedKey.value) && copiedKey.value.value === key;
}

function showCopied(key: string): void {
  copiedKey.value = just(key);
  copiedTimer.schedule(1200, () => {
    if (isJust(copiedKey.value) && copiedKey.value.value === key) {
      copiedKey.value = nothing;
    }
  });
}

async function copyWord(word: string, key: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(word);
    showCopied(key);
  } catch (err) {
    console.error("Thesaurus copy failed", err);
  }
}

function onChipClick(word: string, key: string): void {
  if (lookup.applyReplacementWord(word)) {
    emit("applied");
    return;
  }
  void copyWord(word, key);
}

onUnmounted(() => {
  copiedTimer.clear();
});

defineExpose({
  focus: (options?: FocusOptions) => searchInput.value?.focus(options),
  select: () => searchInput.value?.select(),
});
</script>

<template>
  <div class="zw-thesaurus-body">
    <div
      class="zw-lookup-search"
      :class="{ 'zw-lookup-search--compact': props.compactSearch }"
    >
      <ZwInput
        :id="just('zw-thesaurus-search')"
        ref="searchInput"
        v-model="lookup.query"
        :label="nothing"
        aria-label="Look up a word"
        placeholder="Look up a word…"
        autocomplete="off"
        @keydown.enter.prevent="onSubmit"
      />
      <button
        type="button"
        class="zw-lookup-go"
        :disabled="lookup.loading || lookup.query.trim().length === 0"
        @click="onSubmit"
      >
        Look up
      </button>
    </div>

    <p v-if="lookup.loading" class="zw-lookup-status">Looking up…</p>
    <p
      v-else-if="isJust(lookup.error)"
      class="zw-lookup-status zw-lookup-error"
    >
      {{ lookup.error.value }}
    </p>
    <p
      v-else-if="lookup.lookedUp && lookup.senses.length === 0"
      class="zw-lookup-status"
    >
      No results for “{{ lookup.query }}”.
    </p>
    <p v-else-if="isJust(lookup.resolvedLemma)" class="zw-lookup-status">
      Showing results for “{{ lookup.resolvedLemma.value }}”.
    </p>

    <ul v-if="lookup.senses.length > 0" class="zw-lookup-senses">
      <li
        v-for="(sense, index) in lookup.senses"
        :key="`${sense.pos}-${sense.def}-${index}`"
        class="zw-lookup-sense"
      >
        <div class="zw-lookup-sense-meta">
          <span class="zw-lookup-pos">{{ posLabel(sense.pos) }}</span>
        </div>
        <p class="zw-lookup-def">{{ sense.def }}</p>
        <div v-if="sense.synonyms.length > 0" class="zw-lookup-synonyms">
          <span
            v-for="syn in sense.synonyms"
            :key="syn"
            class="zw-lookup-chip-wrap"
          >
            <button
              type="button"
              class="zw-lookup-chip"
              :title="chipTitle(syn)"
              @click="onChipClick(syn, chipKey('syn', index, syn))"
            >
              {{ syn }}
            </button>
            <span
              v-if="isCopied(chipKey('syn', index, syn))"
              class="zw-lookup-copied"
              role="status"
            >
              Copied!
            </span>
          </span>
        </div>
        <div v-if="sense.antonyms.length > 0" class="zw-lookup-antonyms">
          <span class="zw-lookup-antonyms-label">Antonyms</span>
          <div class="zw-lookup-synonyms">
            <span
              v-for="ant in sense.antonyms"
              :key="ant"
              class="zw-lookup-chip-wrap"
            >
              <button
                type="button"
                class="zw-lookup-chip"
                :title="chipTitle(ant)"
                @click="onChipClick(ant, chipKey('ant', index, ant))"
              >
                {{ ant }}
              </button>
              <span
                v-if="isCopied(chipKey('ant', index, ant))"
                class="zw-lookup-copied"
                role="status"
              >
                Copied!
              </span>
            </span>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.zw-thesaurus-body {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.zw-lookup-search {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  margin: var(--space-sm) var(--space-sm) var(--space-md);
  flex-shrink: 0;
}

.zw-lookup-search--compact {
  flex-direction: row;
  align-items: stretch;
  margin: 0.15rem 0.1rem 0.75rem;
  gap: 0.55rem;
}

.zw-lookup-search--compact :deep(.zw-input-shell) {
  flex: 1;
  min-width: 0;
}

.zw-lookup-go {
  appearance: none;
  border: 1px solid var(--line);
  background: var(--bg-raised);
  color: var(--text);
  font: inherit;
  font-size: 0.8rem;
  font-weight: 500;
  padding: 0.35rem 0.55rem;
  border-radius: 0.25rem;
  cursor: pointer;
  flex-shrink: 0;
}

.zw-lookup-go:hover:not(:disabled) {
  background: var(--hover-fill);
}

.zw-lookup-go:disabled {
  opacity: 0.5;
  cursor: default;
}

.zw-lookup-go:focus-visible {
  outline: 2px solid var(--color-zw-amber);
  outline-offset: 2px;
}

.zw-lookup-status {
  margin: 0 0.35rem 0.5rem;
  font-size: 0.85rem;
  line-height: 1.4;
  color: color-mix(in oklch, var(--text-muted) 85%, transparent);
}

.zw-lookup-error {
  color: var(--text);
}

.zw-lookup-senses {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
  overflow: auto;
  min-height: 0;
}

.zw-lookup-sense-meta {
  margin: 0 0 0.2rem;
}

.zw-lookup-pos {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-muted);
}

.zw-lookup-def {
  margin: 0 0 0.4rem;
  font-size: 0.85rem;
  line-height: 1.4;
  color: var(--text);
}

.zw-lookup-synonyms {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.zw-lookup-antonyms {
  margin-top: 0.45rem;
}

.zw-lookup-antonyms-label {
  display: block;
  margin-bottom: 0.25rem;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--text-muted);
}

.zw-lookup-chip-wrap {
  position: relative;
  display: inline-flex;
  max-width: 100%;
}

.zw-lookup-chip {
  appearance: none;
  border: 1px solid var(--line);
  background: var(--bg-raised);
  color: var(--text);
  font: inherit;
  font-size: 0.78rem;
  line-height: 1.2;
  padding: 0.2rem 0.4rem;
  border-radius: 0.25rem;
  cursor: pointer;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.zw-lookup-chip:hover {
  background: var(--hover-fill);
  border-color: color-mix(in oklch, var(--line) 70%, var(--text-muted));
}

.zw-lookup-chip:focus-visible {
  outline: 2px solid var(--color-zw-amber);
  outline-offset: 2px;
}

.zw-lookup-copied {
  position: absolute;
  left: 50%;
  bottom: calc(100% + 0.3rem);
  transform: translateX(-50%);
  z-index: 2;
  padding: 0.15rem 0.4rem;
  border: 1px solid var(--line);
  border-radius: 0.25rem;
  background: var(--bg-raised);
  color: var(--text-strong);
  font-size: 0.7rem;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  pointer-events: none;
}
</style>
